import React from 'react';
import { View, Pressable, Text, useWindowDimensions, type ViewProps } from 'react-native';
import { cn } from '@/utils/cn';
import { Select, SelectItem } from './select';

export interface PaginationProps extends ViewProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  /** Cor da marca (perfil público). Sem isso, usa a cor primária do app. */
  brandColor?: string;
  className?: string;
}

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const MOBILE_BREAKPOINT = 640;

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  brandColor,
  className,
  ...props
}: PaginationProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;

  // Show fewer page numbers on mobile to avoid overflow
  const maxVisible = isMobile ? 3 : 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);

  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const handleItemsPerPageChange = (value: string) => {
    const newLimit = parseInt(value, 10);
    onItemsPerPageChange?.(newLimit);
    onPageChange(1);
  };

  const buttonSize = isMobile ? 'h-9 w-9' : 'h-10 w-10';
  const buttonTextSize = isMobile ? 'text-lg' : 'text-2xl';
  const gapClass = isMobile ? 'gap-1.5' : 'gap-2';

  const pageControls = (
    <View className={cn('flex-row items-center justify-center', gapClass)}>
      <Pressable
        onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={cn(
          'items-center justify-center rounded-md border border-input bg-background',
          buttonSize,
          'disabled:opacity-50',
          currentPage > 1 && 'hover:bg-primary-100',
        )}
      >
        <Text className={cn('text-foreground mb-0.5', buttonTextSize)}>‹</Text>
      </Pressable>

      {pages.map((page) => (
        <Pressable
          key={page}
          onPress={() => onPageChange(page)}
          className={cn(
            'items-center justify-center rounded-md border',
            buttonSize,
            page === currentPage
              ? !brandColor && 'border-primary bg-primary text-primary-foreground'
              : 'border-input bg-background hover:bg-primary-100',
          )}
          style={
            page === currentPage && brandColor
              ? { backgroundColor: brandColor, borderColor: brandColor }
              : undefined
          }
        >
          <Text
            className={cn(
              'text-sm',
              page === currentPage ? 'font-semibold text-primary-foreground' : 'text-foreground',
            )}
          >
            {page}
          </Text>
        </Pressable>
      ))}

      <Pressable
        onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={cn(
          'items-center justify-center rounded-md border border-input bg-background',
          buttonSize,
          'disabled:opacity-50',
          currentPage < totalPages && 'hover:bg-primary-100',
        )}
      >
        <Text className={cn('text-foreground mb-0.5', buttonTextSize)}>›</Text>
      </Pressable>
    </View>
  );

  const itemsPerPageSelector =
    onItemsPerPageChange && itemsPerPage ? (
      <View className="flex-row items-center gap-2 shrink-0">
        <Text className={cn('text-foreground/70', isMobile ? 'text-xs' : 'text-sm')}>
          Itens por página:
        </Text>
        <Select
          value={itemsPerPage.toString()}
          onValueChange={handleItemsPerPageChange}
          className={isMobile ? 'w-16' : 'w-20'}
        >
          {itemsPerPageOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </Select>
      </View>
    ) : null;

  return (
    <View
      className={cn(
        'flex-row flex-wrap items-center justify-between gap-2',
        className,
      )}
      {...props}
    >
      {pageControls}
      {itemsPerPageSelector}
    </View>
  );
}
